'use client';

import { AppLayout } from '@/components/layouts';
import { ColorModeProvider, ConfirmDialogProvider, PlanLimitGuardProvider } from '@/components/providers';
import { ClarityIdentify, ClarityInit } from '@/components/ui';
import { queryClient } from '@/config/react-query.config';
import { configureOpenApiClient } from '@/lib/api/client/configure-openapi-client';
import '@aws-amplify/ui-react/styles.css';
import setupLocatorUI from '@locator/runtime';
import { CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LicenseInfo } from '@mui/x-license';
import { QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'next/dist/client/components/error-boundary';
import localFont from 'next/font/local';
import { usePathname } from 'next/navigation';
import NextTopLoader from 'nextjs-toploader';
import ErrorPage from './components/ErrorPage';
import ContextProvider from './context/ContextProvider';
import './globals.css';
import './main.css';
configureOpenApiClient();
const neueMontrealFont = localFont({
  src: [{
    path: '../../public/font/NeuMontreal/NeueMontreal-Light.otf',
    weight: '300',
    style: 'normal'
  }, {
    path: '../../public/font/NeuMontreal/NeueMontreal-LightItalic.otf',
    weight: '300',
    style: 'italic'
  }, {
    path: '../../public/font/NeuMontreal/NeueMontreal-Regular.otf',
    weight: '400',
    style: 'normal'
  }, {
    path: '../../public/font/NeuMontreal/NeueMontreal-Italic.otf',
    weight: '400',
    style: 'italic'
  }, {
    path: '../../public/font/NeuMontreal/NeueMontreal-Medium.otf',
    weight: '500',
    style: 'normal'
  }, {
    path: '../../public/font/NeuMontreal/NeueMontreal-MediumItalic.otf',
    weight: '500',
    style: 'italic'
  }, {
    path: '../../public/font/NeuMontreal/NeueMontreal-SemiBold.otf',
    weight: '600',
    style: 'normal'
  }, {
    path: '../../public/font/NeuMontreal/NeueMontreal-Bold.otf',
    weight: '700',
    style: 'normal'
  }, {
    path: '../../public/font/NeuMontreal/NeueMontreal-BoldItalic.otf',
    weight: '700',
    style: 'italic'
  }],
  variable: '--font-neue-montreal'
});
if (process.env.NODE_ENV === 'development') {
  setupLocatorUI();
}
LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI_X_DATA_GRID_LICENSE_KEY);
export const dynamic = 'force-dynamic';
const Layout = ({
  children,
  showHeader,
  hideDefaultLayout = false
}) => {
  if (hideDefaultLayout) {
    return children;
  }
  return <AppLayout showHeader={showHeader}>{children}</AppLayout>;
};
const shouldHideDefaultLayout = pathname => {
  return pathname ? pathname === '/' : false;
};
export default function RootLayout({
  children
}) {
  const pathname = usePathname();
  const hideDefaultLayout = shouldHideDefaultLayout(pathname);
  return <html lang="en" suppressHydrationWarning className={neueMontrealFont.variable} data-scroll-behavior="smooth">
            <head>
                <title>intalent.ai</title>

                <link rel="icon" type="image/x-icon" href="/favicon.ico" />
                <link rel="apple-touch-icon" sizes="180x180" href="/images/brand/apple-touch-icon.png" />
                <link rel="icon" type="image/png" sizes="32x32" href="/images/brand/favicon-32x32.png" />
                <link rel="icon" type="image/png" sizes="16x16" href="/images/brand/favicon-16x16.png" />
                <link rel="manifest" href="/images/brand/site.webmanifest" />
            </head>
            <body className={neueMontrealFont.className}>
                <ClarityInit />
                <NextTopLoader color="#476be1" />
                <QueryClientProvider client={queryClient}>
                    <ErrorBoundary errorComponent={ErrorPage}>
                        <ClarityIdentify />
                        <ContextProvider>
                            <ColorModeProvider fontFamily={neueMontrealFont.style.fontFamily}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <ConfirmDialogProvider>
                                        <PlanLimitGuardProvider>
                                            <Layout showHeader hideDefaultLayout={hideDefaultLayout}>
                                                {children}
                                            </Layout>
                                        </PlanLimitGuardProvider>
                                    </ConfirmDialogProvider>
                                </LocalizationProvider>
                                <CssBaseline />
                            </ColorModeProvider>
                        </ContextProvider>
                    </ErrorBoundary>
                </QueryClientProvider>
            </body>
        </html>;
}